import { DATA, DATA_LABEL, DATA_TAG } from '../../theme/type.js';
import {
  MW_SORTS,
  signed,
  summaryOf,
  trackGroups,
  type MwSortId,
  type MwTrackRow,
} from '../model/mw.js';

/**
 * The launch-event rail (design 738–812).
 *
 * The board's index: every track this seat is warned about, what has happened to
 * it, and — the column that decides the whole turn — how long until it says
 * something new. Sorted by that by default, because it is the only thing on this
 * screen the operator is actually racing.
 *
 * ## Three numbers, and none of them is a score
 *
 * `LIVE` and `CLOSED` count this board. `CREDIT` is the **stage term** banked on
 * it: `MW_STAGE_CREDIT` applied to the stages this seat decided at, and nothing
 * else. The correctness half is graded against the launch's true model at
 * resolution and is not on the wire (M4-D7), and team VP has no evaluator until
 * M6 (F1). A total nobody can compute is a number that will be wrong the first
 * time somebody checks it, so this one is only ever the half that is knowable.
 *
 * ## The open-track marker is the readiness gate, said where it can be fixed
 *
 * `setSubmitted` refuses while any track is open (M4-D13), so the commit control
 * is withdrawn. The reason is drawn *here*, on the row that is causing it, rather
 * than as a sentence beside a disabled button — the operator needs to know which
 * track, and the answer is a one-click fix away.
 */

export interface EventRailProps {
  readonly rows: readonly MwTrackRow[];
  readonly sort: MwSortId;
  readonly onSort: (sort: MwSortId) => void;
  readonly selected: string | null;
  readonly onSelect: (eventId: string) => void;
  /** A fixed column, always: `MW_RAIL_WIDTH` beside a map, `MW_RAIL_WIDTH_ALONE` beside the reading. */
  readonly width: number;
}

export function EventRail(props: EventRailProps): JSX.Element {
  const summary = summaryOf(props.rows);
  const groups = trackGroups(props.rows, props.sort);

  return (
    <div
      data-testid="mw-event-rail"
      data-mat="panel"
      style={{
        flex: 'none',
        width: props.width,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        background: 'var(--surface)',
      }}
    >
      <div
        data-mat="chrome hdr"
        style={{
          flex: 'none',
          height: 33,
          display: 'flex',
          alignItems: 'center',
          padding: '0 12px',
          background: 'var(--surface-2)',
          borderBottom: '1px solid var(--border)',
        }}
      >
        <span style={{ ...DATA_LABEL, flex: 1, color: 'var(--text)' }}>LAUNCH EVENTS</span>
      </div>

      <div
        data-testid="mw-summary"
        style={{
          flex: 'none',
          display: 'flex',
          alignItems: 'baseline',
          gap: 12,
          padding: '8px 12px',
          borderBottom: '1px solid var(--border)',
        }}
      >
        <Stat testId="mw-summary-live" value={String(summary.live)} label="LIVE" tone="warn" />
        <Stat testId="mw-summary-closed" value={String(summary.closed)} label="CLOSED" />
        <span style={{ flex: 1 }} />
        <Stat testId="mw-summary-credit" value={signed(summary.credit)} label="CREDIT" reversed />
      </div>

      <div
        style={{
          flex: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          padding: '6px 12px',
          borderBottom: '1px solid var(--border)',
        }}
      >
        <span style={{ ...DATA_LABEL, flex: 'none', fontWeight: 400, color: 'var(--text-muted)' }}>
          SORT
        </span>
        {MW_SORTS.map((sort) => (
          <button
            key={sort.id}
            type="button"
            data-testid={'mw-sort-' + sort.id}
            aria-pressed={props.sort === sort.id}
            onClick={() => props.onSort(sort.id)}
            style={{
              ...DATA_TAG,
              flex: 'none',
              height: 20,
              padding: '0 7px',
              background: props.sort === sort.id ? 'var(--surface-3)' : 'transparent',
              border:
                '1px solid ' + (props.sort === sort.id ? 'var(--accent-border)' : 'var(--border)'),
              borderRadius: 'var(--radius)',
              color: props.sort === sort.id ? 'var(--text)' : 'var(--text-muted)',
              cursor: 'pointer',
            }}
          >
            {sort.label}
          </button>
        ))}
      </div>

      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {groups.length === 0 && (
          <div
            data-testid="mw-no-tracks"
            style={{
              ...DATA_LABEL,
              padding: '20px 12px',
              textAlign: 'center',
              color: 'var(--text-muted)',
            }}
          >
            NO LAUNCH EVENTS
          </div>
        )}
        {groups.map((group) => (
          <div key={group.id} data-testid="mw-group" data-group={group.id}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '7px 12px 4px',
                background: 'var(--surface-2)',
                borderBottom: '1px solid var(--rule)',
              }}
            >
              <span style={{ ...DATA_LABEL, flex: 1, color: 'var(--text-muted)' }}>
                {group.label}
              </span>
              <span
                data-testid={'mw-group-count-' + group.id}
                style={{ ...DATA, fontWeight: 400, color: 'var(--text-muted)' }}
              >
                {group.rows.length}
              </span>
            </div>
            {group.rows.map((row) => (
              <TrackRow
                key={row.eventId}
                row={row}
                selected={row.eventId === props.selected}
                onSelect={props.onSelect}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function TrackRow(props: {
  readonly row: MwTrackRow;
  readonly selected: boolean;
  readonly onSelect: (eventId: string) => void;
}): JSX.Element {
  const row = props.row;
  return (
    <button
      type="button"
      data-testid="mw-track"
      data-event={row.eventId}
      data-state={row.state}
      data-stage={row.stage}
      data-briefed={row.briefed ? 'true' : undefined}
      data-open={row.open ? 'true' : undefined}
      aria-current={props.selected ? 'true' : undefined}
      onClick={() => props.onSelect(row.eventId)}
      style={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        padding: '7px 12px',
        background: props.selected ? 'var(--surface-3)' : 'transparent',
        border: 0,
        borderLeft: '2px solid ' + (props.selected ? 'var(--accent)' : 'transparent'),
        borderBottom: '1px solid var(--rule-faint)',
        textAlign: 'left',
        cursor: 'pointer',
      }}
    >
      <span style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <span
          data-testid="mw-track-id"
          style={{
            ...DATA,
            flex: 1,
            minWidth: 0,
            fontSize: 12,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            color: row.state === 'DISMISSED' ? 'var(--text-2)' : 'var(--red)',
          }}
        >
          {row.eventId}
        </span>
        {/* The stage pips: four, and a pip is either observed or pending. Never a
            third state — a stage has fired or it has not. */}
        <span data-testid="mw-pips" style={{ flex: 'none', display: 'flex', gap: 3 }}>
          {row.pips.map((pip) => (
            <span
              key={pip.stage}
              data-testid="mw-pip"
              data-stage={pip.stage}
              data-pip={pip.state}
              style={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                background: pip.state === 'OBSERVED' ? 'var(--accent)' : 'transparent',
                border:
                  '1px solid ' +
                  (pip.state === 'OBSERVED' ? 'var(--accent)' : 'var(--border-strong)'),
              }}
            />
          ))}
        </span>
      </span>

      <span style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <span
          data-testid="mw-track-identity"
          style={{
            flex: 1,
            minWidth: 0,
            fontFamily: 'var(--font-prose)',
            fontSize: 12,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            color: 'var(--text-muted)',
          }}
        >
          {row.identity ?? 'UNIDENTIFIED'}
        </span>
        {/* Why the commit control is withdrawn, on the row causing it (M4-D13). */}
        {row.open && (
          <span
            data-testid="mw-track-open"
            style={{ ...DATA_LABEL, flex: 'none', fontSize: 10, color: 'var(--warn)' }}
          >
            OWES A DECISION
          </span>
        )}
        {row.state !== 'LIVE' && (
          <span
            data-testid="mw-track-banked"
            style={{ ...DATA, flex: 'none', fontSize: 10, color: 'var(--text-muted)' }}
          >
            {row.state} {row.creditBanked === null ? '—' : signed(row.creditBanked)}
          </span>
        )}
        {row.next !== null && (
          <span
            data-testid="mw-track-next"
            data-stage={row.next.stage}
            style={{ ...DATA, flex: 'none', color: 'var(--text-2)' }}
          >
            S{row.next.stage} {row.next.clock}
          </span>
        )}
      </span>
    </button>
  );
}

function Stat(props: {
  readonly testId: string;
  readonly value: string;
  readonly label: string;
  readonly tone?: 'warn';
  readonly reversed?: boolean;
}): JSX.Element {
  const value = (
    <span
      data-testid={props.testId}
      style={{
        ...DATA,
        fontSize: 13,
        color: props.tone === 'warn' ? 'var(--warn)' : 'var(--text-2)',
      }}
    >
      {props.value}
    </span>
  );
  const label = (
    <span style={{ ...DATA_LABEL, fontWeight: 400, color: 'var(--text-muted)' }}>
      {props.label}
    </span>
  );
  return (
    <span style={{ display: 'flex', alignItems: 'baseline', gap: 5 }}>
      {props.reversed === true ? label : value}
      {props.reversed === true ? value : label}
    </span>
  );
}
